import { useCallback, useEffect, useRef, useState } from "react";
import {
  CHAT_BUILD_VERSION,
  CHAT_BUILD_VERSION_LS_KEY,
} from "@/lib/cache/chatBuildVersion";

const UPDATE_EVENT = "chat-sw-update-available";
const UPDATE_PHASE_EVENT = "chat-sw-update-phase";
const UPDATE_APPLY_FLAG = "chat_update_apply_v1";

export type ChatUpdatePhase = "idle" | "available" | "preparing" | "activating" | "reloading";

function readTargetBuildVersion(): string {
  return CHAT_BUILD_VERSION;
}

function buildUpdateNeeded(stored: string | null): boolean {
  const target = readTargetBuildVersion();
  if (stored == null) return false;
  return stored !== target;
}

function readStoredBuildVersion(): string | null {
  try {
    return localStorage.getItem(CHAT_BUILD_VERSION_LS_KEY);
  } catch {
    return null;
  }
}

function writeStoredBuildVersion(version: string): void {
  try {
    localStorage.setItem(CHAT_BUILD_VERSION_LS_KEY, version);
  } catch {
    /* ignore */
  }
}

function markUpdateApplyInProgress(): void {
  try {
    sessionStorage.setItem(UPDATE_APPLY_FLAG, String(Date.now()));
  } catch {
    /* ignore */
  }
}

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** MAJ app — feedback visible puis reload explicite. */
export function useChatSwUpdate() {
  const [phase, setPhase] = useState<ChatUpdatePhase>("idle");
  const applyLock = useRef(false);

  const updateReady = phase === "available";

  useEffect(() => {
    const onSwUpdate = () => {
      setPhase((prev) => (prev === "idle" ? "available" : prev));
    };
    window.addEventListener(UPDATE_EVENT, onSwUpdate);
    return () => window.removeEventListener(UPDATE_EVENT, onSwUpdate);
  }, []);

  useEffect(() => {
    const onPhase = (event: Event) => {
      const detail = (event as CustomEvent<{ phase?: string }>).detail;
      const next = detail?.phase;
      if (next === "activating" || next === "waiting") {
        setPhase("activating");
      } else if (next === "reloading") {
        setPhase("reloading");
      }
    };
    window.addEventListener(UPDATE_PHASE_EVENT, onPhase);
    return () => window.removeEventListener(UPDATE_PHASE_EVENT, onPhase);
  }, []);

  useEffect(() => {
    const stored = readStoredBuildVersion();
    const target = readTargetBuildVersion();
    if (stored == null) {
      writeStoredBuildVersion(target);
      return;
    }
    if (buildUpdateNeeded(stored)) {
      setPhase("available");
    }
  }, []);

  const applyUpdate = useCallback(async () => {
    if (applyLock.current || phase !== "available") return;
    applyLock.current = true;
    markUpdateApplyInProgress();
    setPhase("preparing");

    const failSafe = window.setTimeout(() => {
      applyLock.current = false;
      setPhase("idle");
      try {
        sessionStorage.removeItem(UPDATE_APPLY_FLAG);
      } catch {
        /* ignore */
      }
    }, 20_000);

    try {
      await waitForPaint();
      await sleep(280);

      writeStoredBuildVersion(readTargetBuildVersion());

      setPhase("activating");

      try {
        const apply = (
          window as Window & { chatPwaApplyUpdate?: () => Promise<unknown> }
        ).chatPwaApplyUpdate;
        if (apply) {
          await apply();
          return;
        }
      } catch {
        /* fallback reload */
      }

      setPhase("reloading");
      await sleep(600);
      window.location.reload();
    } finally {
      window.clearTimeout(failSafe);
    }
  }, [phase]);

  /** Overlay MAJ coincé (reload raté) : reprendre la main. */
  useEffect(() => {
    if (phase !== "preparing" && phase !== "activating" && phase !== "reloading") {
      return;
    }
    const t = window.setTimeout(() => {
      applyLock.current = false;
      setPhase("idle");
      try {
        sessionStorage.removeItem(UPDATE_APPLY_FLAG);
      } catch {
        /* ignore */
      }
    }, 22_000);
    return () => window.clearTimeout(t);
  }, [phase]);

  const forceReload = useCallback(() => {
    applyLock.current = false;
    try {
      sessionStorage.removeItem(UPDATE_APPLY_FLAG);
    } catch {
      /* ignore */
    }
    writeStoredBuildVersion(readTargetBuildVersion());
    window.location.reload();
  }, []);

  return {
    phase,
    updateReady,
    applying: phase === "preparing" || phase === "activating" || phase === "reloading",
    applyUpdate,
    forceReload,
  };
}
